import { Icon, useModalState } from '@bechtel/gds';
import { ListType } from '@bechtel/sr-types';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { find, get, includes, isNil } from 'lodash';
import { PropsWithChildren, useState } from 'react';
import { SelectGridModal, useTableMetadataContext } from '~/components';
import { PickListComponentProps } from '../../types';
import { isFieldEditable } from '../../utils';
import { GridCellContainer, PickListButtonStyled } from './styled';

export const PickListCellRenderer = (props: PropsWithChildren<PickListComponentProps & ICellRendererParams>) => {
  const { onRowEdited, value, setValue, column, rowHeight, children, colDef, node, api, cellEditing } = props;

  const cellValue = value;
  const { visible, open, close } = useModalState(false);
  const tableMetadata = useTableMetadataContext();

  const metadataColumn = find(tableMetadata?.metadataColumns, (item) => item?.memberName == column?.getColId());

  const [hover, setHover] = useState(false);

  const selectItem = async (item) => {
    const selectedColumn = column?.getColId();
    if (!selectedColumn) return;
    const validateColData = colDef?.cellEditorParams.validationFns?.[selectedColumn];
    // No need for the picklistKeys if your column is not of complex type (ex. package.tag, cable.reel)
    // it will automatically take memberName if you dont pass
    const picklistObj = colDef?.cellEditorParams.picklistKeys[selectedColumn];
    //  selectKey is the key you want to pick from the item. Default will be the column memberName/colId
    const selectKey = picklistObj?.selectKey ?? selectedColumn;
    //  returnKey is the key that you want to send in OnRowEdited. Default will be the column memberName/colId
    //  you get this key as a returned object in the rowData.
    // For wxample: package.tag will have return key as package, selectKey as tag
    const returnKey = picklistObj?.returnKey ?? selectedColumn;
    // If you want the whole object returned as value
    const expectsObj = picklistObj?.expectsObj ?? false;

    if (validateColData) {
      const isPickList = !includes([ListType.CONTROLLED, ListType.NONE], metadataColumn?.pickListType);
      const property = isPickList ? 'value' : selectKey;
      const result = await validateColData(item[property], props, true);
      if (result) {
        validationSuccess(returnKey, item, expectsObj);
      }
    } else {
      validationSuccess(returnKey, item, expectsObj);
    }

    setHover(false);
    props.api.setFocusedCell(node.rowIndex ?? 0, selectedColumn);
    close();
  };

  const validationSuccess = (selectedColumn, item, expectsObj) => {
    const isPickList = !includes([ListType.CONTROLLED, ListType.NONE], metadataColumn?.pickListType);
    const value = isPickList ? item.value : item[selectedColumn];
    const selectedRowData = find(api?.getSelectedRows(), (item) => item.id === node.data.id) ?? node.data;

    if (selectedColumn && onRowEdited)
      onRowEdited({
        ...selectedRowData,
        [selectedColumn]: expectsObj ? item : value,
      });

    // setting the grid cell data through custom cell editor
    if (setValue && metadataColumn?.memberName)
      setValue(
        // if fields has relatedMemberDisplayField (Ex: cableCode, serviceLevel)
        metadataColumn?.relatedMemberDisplayField
          ? item[metadataColumn?.relatedMemberDisplayField]
          : // if key is memberName (Ex: layoutDrawing, misc fields)
            get(item, metadataColumn?.memberName) ?? value // else value(Ex: secondaryStatus)
      );

    // when selecting picklist from server grid and the field has relatedMember then selected data need to set to grid using setData
    if (metadataColumn?.relatedMemberName && colDef) {
      node?.setData({
        ...selectedRowData,
        [selectedColumn]: item,
      });
    }
  };

  const editable = isFieldEditable(colDef?.editable, props);

  const handleClick = () => {
    // open the picklist if the cell is not in editing mode
    if (!cellEditing) {
      open();
      return;
    }

    // stopping the cell editing, and then clicking on the picklist icon using DOM
    // otherwise the picklist will not work
    // Reason: while the cell is in editing mode, CustomCellEditor is getting rendered,
    // >>> once the picklist button is clicked, the picklist opens
    // >>> clicking anywhere will stop the cell from editing and it will cause the CustomCellEditor to unmount, hence the picklist will close
    // >>> Hence manually stopping the cell Edit and then clicking on the picklist icon again
    api.stopEditing();
    setTimeout(() => {
      const focusedCell = api.getFocusedCell();
      if (focusedCell) {
        const { rowIndex, column } = focusedCell;
        // selecting the cell element
        const cellElement = document.querySelector(`.ag-row[row-index="${rowIndex}"] [col-id="${column.getColId()}"]`);
        // targeting the popperButton
        // "popperButton" is the class assigned to picklist icon.
        // triggering a click on the popperButton element to open the picklist

        const popperButton = cellElement?.querySelector('.popperButton');

        if (popperButton && colDef?.editable) {
          (popperButton as HTMLElement).click();
        }
      }
    }, 100);
  };

  return (
    <>
      <GridCellContainer rowHeight={rowHeight} onMouseOver={() => setHover(true)} onMouseLeave={() => setHover(false)}>
        {children ? children : cellValue}
        {metadataColumn?.pickListName && !isNil(metadataColumn?.pickListType) && (
          <PickListButtonStyled show={editable && hover} className="popperButton" onClick={handleClick}>
            <Icon icon="ellipsis" iconStyle="solid" fillColor="primary.700" />
          </PickListButtonStyled>
        )}
      </GridCellContainer>
      {visible && !!metadataColumn?.pickListName && !isNil(metadataColumn?.pickListType) && (
        <SelectGridModal
          close={() => {
            setHover(false);
            close();
            api.setFocusedCell(node.rowIndex ?? 0, column?.getColId() ?? '');
          }}
          title="Select"
          listType={metadataColumn?.pickListType}
          selectItem={selectItem}
          listName={metadataColumn?.pickListName}
          defaultFilterValue={colDef?.picklistFilter ? colDef?.picklistFilter() : {}}
          rowSelection={colDef?.cellEditorParams?.rowSelection}
        />
      )}
    </>
  );
};
